import { useState } from 'react';
import type { As400ExportGroup, ListAs400ExportResponse } from '@erp/rpc';
import { skipToken, useQueries, useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import {
  extraAs400PageCount,
  ITEM_ADD_INDEX_PAGE_SIZE,
  itemAddIndexView,
} from './itemAddIndexPages';

type IndexGroup = Extract<As400ExportGroup, 'ITEM' | 'ITEM_ADD'>;

function itemAddIndexQuery(input: {
  group: IndexGroup;
  dataSourceIds: string[];
  changeSetId: string;
  page: number;
  enabled: boolean;
}) {
  return {
    ...orpc.inventoryManagement.products.listAs400Export.queryOptions({
      input: input.enabled
        ? {
            group: input.group,
            dataSourceIds: input.dataSourceIds,
            page: input.page,
            pageSize: ITEM_ADD_INDEX_PAGE_SIZE,
            advancedFilters: [],
            changeSetId: input.changeSetId,
          }
        : skipToken,
    }),
    enabled: input.enabled,
  };
}

const NO_ITEM_ADD_INDEX = {
  data: { rows: [], newRows: [] },
  isLoading: false,
  error: null,
};

function itemAddPageStates(
  results: readonly {
    data?: ListAs400ExportResponse;
    error: unknown;
    isPending: boolean;
  }[],
) {
  return results.map(({ data, error, isPending }) => ({
    data,
    error,
    isPending,
  }));
}

function useAs400WorksheetIndex(
  group: IndexGroup,
  dataSourceIds: string[],
  changeSetId: string,
) {
  const enabled = dataSourceIds.length > 0;
  const first = useQuery(
    itemAddIndexQuery({
      group,
      dataSourceIds,
      changeSetId,
      page: 1,
      enabled,
    }),
  );
  const [extraCount, setExtraCount] = useState(0);
  const rest = useQueries({
    queries: Array.from({ length: extraCount }, (_, i) =>
      itemAddIndexQuery({
        group,
        dataSourceIds,
        changeSetId,
        page: i + 2,
        enabled,
      }),
    ),
    combine: itemAddPageStates,
  });
  const nextExtra = extraAs400PageCount(
    first.data,
    rest.map((page) => page.data),
  );
  if (nextExtra !== extraCount) setExtraCount(nextExtra);
  const extraSettled = nextExtra === extraCount;
  const { data, error, isPending } = first;
  const view = itemAddIndexView({ data, error, isPending }, rest, extraSettled);
  return enabled ? view : NO_ITEM_ADD_INDEX;
}

export function useAs400ItemAddIndex(
  dataSourceIds: string[],
  changeSetId: string,
) {
  return useAs400WorksheetIndex('ITEM_ADD', dataSourceIds, changeSetId);
}

export function useAs400ItemSheetIndex(
  dataSourceIds: string[],
  changeSetId: string,
) {
  return useAs400WorksheetIndex('ITEM', dataSourceIds, changeSetId);
}
